import { ProductTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import BaseTabs from "@ui-kit/disclosure/BaseTabs/BaseTabs.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import ParsedHtml from "@ui-kit/typography/ParsedHtml/ParsedHtml.web";
import { Dispatch, SetStateAction, useMemo, useRef } from "react";
import { useTranslation } from "@shared/utils/internationalization";
import ProductAddonsSelectorCard from "./ProductAddonsSelectorCard";

const ProductAddonsDescriptionPanel = ({
  product,
}: {
  product: ProductTypeSerializer;
}) => {
  const { description } = product;
  const { t } = useTranslation("purchases");
  return (
    <Flex borderRadius="sm" bg="bg" mx="xs" p="md">
      <ParsedHtml htmlString={description || t("No description available.")} />
    </Flex>
  );
};

const ProductAddonsOptionsPanel = ({
  product,
  variantQuantityAdded,
  setVariantQuantityAdded,
}: {
  product: ProductTypeSerializer;
  variantQuantityAdded: Record<string, number>;
  setVariantQuantityAdded: Dispatch<SetStateAction<Record<string, number>>>;
}) => {
  const { product_attributes: options, currency } = product;
  const validOptions = useMemo(
    () =>
      (options || []).filter(
        (o) =>
          o.inventory &&
          o.inventory > 0 &&
          o.purchase_limit &&
          o.purchase_limit > 0 &&
          !o.sold_out
      ),
    [options]
  );

  return (
    <Flex direction="column" gap="md">
      {validOptions.map((variant) => {
        return (
          <ProductAddonsSelectorCard
            key={variant.id}
            variant={variant}
            currency={currency}
            amountSelected={variantQuantityAdded[variant.id] || 0}
            setAmountSelected={({ amount }) => {
              setVariantQuantityAdded((prevQuantities) => ({
                ...prevQuantities,
                [variant.id.toString()]: amount,
              }));
            }}
          />
        );
      })}
    </Flex>
  );
};

export interface AddonProductTabsProps {
  product: ProductTypeSerializer;
  variantQuantityAdded: Record<string, number>;
  setVariantQuantityAdded: Dispatch<SetStateAction<Record<string, number>>>;
  hideDescription?: boolean;
}

const AddonProductTabs = ({
  product,
  variantQuantityAdded,
  setVariantQuantityAdded,
  hideDescription,
}: AddonProductTabsProps) => {
  const { t } = useTranslation("purchases");

  const panelRefs = useRef<HTMLDivElement | null>(null);
  /**
   * This is a handler to calculate the minHeight of the tabs container.
   * The minHeight is calculated based on the first tab that holds the items
   */
  const minHeight = useMemo(() => {
    if (panelRefs.current) {
      if (panelRefs.current.scrollHeight > 600) return "40vh";
      return panelRefs.current.scrollHeight;
    }
    return "auto";
  }, [panelRefs.current]);

  const hasDescription = Boolean(product?.description) && !hideDescription;

  return (
    <BaseTabs isFitted h="100%">
      <BaseTabs.TabList bg="bg" position="sticky" top="none" zIndex="1">
        {hasDescription && (
          <>
            <BaseTabs.Tab>{t("Buy product")}</BaseTabs.Tab>
            <BaseTabs.Tab>{t("Description")}</BaseTabs.Tab>
          </>
        )}
      </BaseTabs.TabList>
      <BaseTabs.TabPanels
        overflowY="auto"
        flex="1"
        minHeight={{
          base: "auto",
          sm: minHeight,
        }}
      >
        <BaseTabs.TabPanel ref={panelRefs} padding={{ base: "lg", sm: "xl" }}>
          <ProductAddonsOptionsPanel
            product={product}
            variantQuantityAdded={variantQuantityAdded}
            setVariantQuantityAdded={setVariantQuantityAdded}
          />
        </BaseTabs.TabPanel>
        <BaseTabs.TabPanel>
          <ProductAddonsDescriptionPanel product={product} />
        </BaseTabs.TabPanel>
      </BaseTabs.TabPanels>
    </BaseTabs>
  );
};

export {
  AddonProductTabs,
  ProductAddonsDescriptionPanel,
  ProductAddonsOptionsPanel,
};
